import { inject, Injectable } from '@angular/core';
import { createValidationRun } from '@epicuro-next/addons/sandbox';
import { ExtendedDtoBasket } from '@epicuro-next/data/baskets-dto';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { ValidationResult } from 'graphql-api/gql-api-interfaces';
import { combineLatest, forkJoin, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';

import { ValidateBasketPayload } from '../../models/validate-basket-payload';
import {
  groupDtoBasketSuccess,
  runBasketValidations,
  runBasketValidationsSuccess,
} from '../actions/index';
import {
  selectBasketValidatorFunctionsRule,
  selectCanBasketBeChanged,
  selectDeliveryShopTotals,
  selectOperableDtoBasketFromEntity,
} from '../selectors';

@Injectable()
export class BasketValidationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly logger = inject(LoggerService);

  public runBasketValidationFunctions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(groupDtoBasketSuccess),
      debounceTime(1500),
      map(() => runBasketValidations()),
    ),
  );

  public validateBasket$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(runBasketValidations))]).pipe(
      concatLatestFrom(() => [
        this.store.pipe(select(selectBasketValidatorFunctionsRule)),
        this.store.pipe(select(selectCanBasketBeChanged)),
      ]),
      filter(
        ([, validators, canBeChanged]) =>
          canBeChanged && validators && !!validators.length,
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectOperableDtoBasketFromEntity)),
        this.store.pipe(select(selectDeliveryShopTotals)),
      ]),
      switchMap(([[, validators], basket, shopsTotals]) => {
        return this.validateBasket({
          validators,
        })({
          basket: basket as ExtendedDtoBasket,
          shopsTotals,
        });
      }),
    ),
  );

  private validateBasket({ validators }: { validators: string[] }) {
    return (payload: ValidateBasketPayload) => {
      return forkJoin(
        validators.map((validatorFn: string) =>
          createValidationRun<ValidationResult>(
            validatorFn,
            payload,
            this.logger,
            3000,
          ),
        ),
      ).pipe(
        map((validationResults) => {
          return validationResults.flat();
        }),
        // questionable actually either we have to return it as it
        // and as a result possible validation error with passed value
        catchError(() => of([])),
        map((validationResults: ValidationResult[]) => {
          return runBasketValidationsSuccess({
            validationResults,
          });
        }),
      );
    };
  }
}
